/**
 * Keeps the user's position out of Sentry (spec §16 rule 7, ruling W32). "Sheets here" sent the position to our API
 * as `?at=<lat>,<lon>` until W35/R47 moved it to the X-Position header (which Sentry does not record); an old client's
 * URL still can, so this stays. Sentry's breadcrumbs, transactions (span descriptions and url data) and replay network events
 * would otherwise copy that URL to a third party. Every string in what Sentry is about to keep has its `at` query
 * value replaced by `redacted`; nothing else changes.
 */

const AT_PARAM = /([?&](?:at|%61t|a%74|%61%74)=)[^&#\s"']*/gi;
const MAX_DEPTH = 12;

/** `…/sheets?q=C&at=39.99,-104.84` → `…/sheets?q=C&at=redacted` (any number of `at`, other parameters kept) */
export function scrubAtParam(value: string): string {
  return value.includes('=') ? value.replace(AT_PARAM, '$1redacted') : value;
}

/**
 * Scrubs `at` from every string inside a Sentry breadcrumb, event, transaction or replay recording event, in place,
 * and returns it (Sentry's hooks take the returned value). Cycles and very deep nesting are left alone.
 */
export function scrubLocation<T>(value: T): T {
  try {
    return walk(value, 0, new WeakSet<object>()) as T;
  } catch {
    // Never drop the event: keep it, without the URLs that could carry the position
    return dropUrls(value);
  }
}

const URL_KEYS = ['url', 'http.url', 'from', 'to'];

/** The fallback when scrubbing threw (a throwing getter, say): remove URL fields where Sentry puts them */
function dropUrls<T>(value: T): T {
  const strip = (o: unknown) => {
    if (o && typeof o === 'object') {
      for (const key of URL_KEYS) {
        try {
          delete (o as Record<string, unknown>)[key];
        } catch {
          // a frozen or exotic object: leave it
        }
      }
    }
  };
  try {
    const v = value as unknown as { request?: unknown; data?: unknown; breadcrumbs?: unknown[]; spans?: { data?: unknown }[] };
    strip(v);
    strip(v?.request);
    strip(v?.data);
    (Array.isArray(v?.breadcrumbs) ? v.breadcrumbs : []).forEach(b => strip((b as { data?: unknown })?.data));
    (Array.isArray(v?.spans) ? v.spans : []).forEach(sp => strip(sp?.data));
  } catch {
    // nothing more to try
  }
  return value;
}

function walk(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return scrubAtParam(value);
  }
  if (!value || typeof value !== 'object' || depth > MAX_DEPTH || seen.has(value)) {
    return value;
  }
  seen.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = walk(value[i], depth + 1, seen);
    }
    return value;
  }
  // Only plain data: leave class instances (DOM nodes, errors' internals) as they are
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = walk(record[key], depth + 1, seen);
  }
  return value;
}
